import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ScrollText, ShieldCheck, LogOut, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { collectClientInfo } from "@/lib/clientInfo";
import { captureAcceptLocation } from "@/lib/locationCapture";
import { toast } from "sonner";
import { TermsBody, useTosVersion } from "@/components/TermsBody";
import { useReleaseMetadata } from "@/hooks/useReleaseMetadata";

const TOS_OK_KEY = "playden_tos_ok";

/**
 * Per-login Terms of Service gate. Every fresh login session must re-accept
 * the CURRENT build's ToS version; declining signs the user out. The gate also
 * re-fires mid-session if the server's ToS version no longer matches the version
 * the user last accepted. Acceptance is recorded server-side with the full
 * forensic trail (version, timestamp, IP chain, headers, device fingerprint).
 */
export function TosGate() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const version = useTosVersion();
  const { data: metadata, isError: metadataError, refetch: refreshMetadata } = useReleaseMetadata();
  const [acceptedVersion, setAcceptedVersion] = useState(() => sessionStorage.getItem(TOS_OK_KEY));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Re-evaluate whenever the signed-in user changes (fresh login in same tab).
  useEffect(() => {
    setAcceptedVersion(sessionStorage.getItem(TOS_OK_KEY));
  }, [user?.id]);

  // Version check: what did this account last accept vs. the current build?
  const { data: status } = useQuery({
    queryKey: ["tos-status", user?.id, version],
    queryFn: async () => (await api.get("/tos/status")).data,
    enabled: !!user,
    staleTime: 30_000,
  });
  const versionMismatch = !!status && status.accepted_version !== version;

  const show = !!user && (!metadata || acceptedVersion !== version || versionMismatch);

  // Lock body scroll while the gate is up.
  useEffect(() => {
    if (show) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [show]);

  if (!show) return null;

  const accept = async () => {
    if (!metadata || metadataError || busy) return;
    setBusy(true);
    setError("");
    let client_info = null;
    try { client_info = await collectClientInfo(); } catch (_) { /* best-effort */ }
    // ToS v.013+ — capture the acceptor's location (browser-precise if the
    // user grants, otherwise server-side IP fallback). Server rejects the
    // acceptance if it can't derive any location; we surface that error and
    // keep the gate up so the user can retry after enabling location.
    let location = null;
    try { location = await captureAcceptLocation(); } catch (_) { location = null; }
    try {
      const { data } = await api.post("/tos/accept", { tos_version: version, client_info, location });
      if (!data.ok || data.version !== version) throw new Error("Agreement version changed. Please review it again.");
      sessionStorage.setItem(TOS_OK_KEY, data.version);
      setAcceptedVersion(data.version);
      await queryClient.invalidateQueries({ queryKey: ["tos-status"] });
    } catch (err) {
      const message = apiError(err, "Acceptance was not saved. Please try again.");
      setError(message);
      toast.error(message);
      if (err?.response?.status === 409) await refreshMetadata();
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (await logout()) navigate("/");
  };

  return (
    <div
      data-testid="tos-gate"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(4, 6, 10, 0.88)", backdropFilter: "blur(8px)" }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl border border-white/10 bg-[hsl(222_22%_8%)] shadow-[0_30px_90px_rgba(0,0,0,0.7)] overflow-hidden"
        data-testid="tos-gate-dialog"
      >
        <div className="relative px-6 sm:px-8 pt-7 pb-5 border-b border-white/10 shrink-0">
          <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-cyan-400/15 border border-cyan-300/30 flex items-center justify-center shrink-0">
              <ScrollText className="h-6 w-6 text-cyan-300" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">Terms of Service</span>
                <span data-testid="tos-gate-version" className="font-mono text-[9px] uppercase tracking-[0.2em] text-black bg-cyan-300 rounded-full px-2 py-0.5">V {version}</span>
                {versionMismatch && status?.accepted_version && (
                  <span data-testid="tos-previous-version" className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-300 border border-amber-300/40 rounded-full px-2 py-0.5">
                    Updated since V {status.accepted_version}
                  </span>
                )}
              </div>
              <h2 className="font-display text-2xl text-white tracking-[0.05em] mt-1">AGREE TO CONTINUE.</h2>
              <p className="text-white/50 text-xs mt-1">
                Acceptance is required at every login and is recorded with a timestamp, IP, device details, and location. Declining will sign you out.
              </p>
              <p className="text-white/35 text-[10px] mt-1 font-mono uppercase tracking-[0.2em] inline-flex items-center gap-1">
                <MapPin className="h-2.5 w-2.5" /> Your browser may ask for location — permission is required for compliance.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-5 flex-1 min-h-0 overflow-y-auto" data-testid="tos-gate-terms">
          <TermsBody />
        </div>

        {(error || metadataError) && <p data-testid="tos-accept-error" role="alert" className="px-6 sm:px-8 pt-3 text-sm text-red-300">{error || "Current agreement unavailable. Try again."}</p>}
        {metadataError && <Button data-testid="tos-version-retry" variant="ghost" onClick={() => refreshMetadata()}>Retry</Button>}
        <div className="px-6 sm:px-8 py-5 border-t border-white/10 flex flex-col-reverse sm:flex-row gap-3 shrink-0">
          <Button
            data-testid="tos-gate-decline"
            variant="outline"
            onClick={decline}
            disabled={busy}
            className="flex-1 border-white/15 text-white/70 hover:bg-white/5"
          >
            <LogOut className="h-4 w-4 mr-1.5" /> I do not agree — sign me out
          </Button>
          <Button
            data-testid="tos-gate-agree"
            onClick={accept}
            disabled={busy || !metadata || metadataError}
            className="flex-1 bg-cyan-400 text-black hover:bg-cyan-300 font-medium"
          >
            <ShieldCheck className="h-4 w-4 mr-1.5" /> {busy ? "Recording acceptance…" : `I agree to the Terms (V ${version})`}
          </Button>
        </div>
      </div>
    </div>
  );
}
